import React, { useEffect, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Replay as ReplayIcon,
    Schedule as ScheduleIcon
} from "@mui/icons-material";
import { getSurveyStatusForPassenger } from "../../../../api/surveys/surveyApi";

/**
 * Celda de estado de la encuesta para un pasajero.
 * - Consulta el estado al montar (y cuando cambia retryVersion).
 * - Muestra chip verde (ENVIADO), rojo (FALLIDO), gris (PENDIENTE/null).
 * - Si está FALLIDO, muestra botón de reintento.
 */
const PassengerSurveyCell = ({
    manifestId,
    passengerId,
    onRetry,
    isRetrying = false,
    retryVersion = 0
}) => {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchStatus = async () => {
            if (!manifestId || !passengerId) return;

            setLoading(true);
            try {
                const result = await getSurveyStatusForPassenger(manifestId, passengerId);
                if (!cancelled) setState(result);
            } catch (err) {
                console.error("Error consultando estado de encuesta:", err);
                if (!cancelled) setState({ status: null, sent: false, error: "Error al consultar" });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchStatus();

        return () => { cancelled = true; };
    }, [manifestId, passengerId, retryVersion]);

    if (loading && !state) {
        return <CircularProgress size={16} />;
    }

    const status = state?.status;

    // Sin registro → sin chip, solo un texto
    if (!status) {
        return (
            <Tooltip title="Sin registro de encuesta">
                <Typography variant="caption" color="text.disabled">—</Typography>
            </Tooltip>
        );
    }

    const chipProps = (() => {
        switch (status) {
            case 'ENVIADO':
                return {
                    icon: <CheckCircleIcon fontSize="small" />,
                    label: 'Enviada',
                    color: 'success',
                    tooltip: state.sentAt ? `Enviada el ${new Date(state.sentAt).toLocaleString('es-PE')}` : 'Enviada'
                };
            case 'FALLIDO':
                return {
                    icon: <ErrorIcon fontSize="small" />,
                    label: 'Falló',
                    color: 'error',
                    tooltip: state.error || 'Error al enviar la encuesta'
                };
            case 'PENDIENTE':
                return {
                    icon: <ScheduleIcon fontSize="small" />,
                    label: 'Pendiente',
                    color: 'warning',
                    tooltip: 'Pendiente de envío'
                };
            default:
                return {
                    icon: null,
                    label: status,
                    color: 'default',
                    tooltip: status
                };
        }
    })();

    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <Tooltip title={chipProps.tooltip}>
                <Chip
                    size="small"
                    icon={chipProps.icon}
                    label={chipProps.label}
                    color={chipProps.color}
                    variant="outlined"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                />
            </Tooltip>

            {status === 'FALLIDO' && onRetry && (
                <Tooltip title="Reintentar envío">
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            disabled={isRetrying}
                            onClick={() => onRetry(passengerId)}
                        >
                            {isRetrying ? <CircularProgress size={14} /> : <ReplayIcon fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
};

export default PassengerSurveyCell;